// ─── World props rendering ────────────────────────────────────────────────────
// Decorative props (trees, rocks, bushes, signposts...) placed by
// src/data/world-props.ts and drawn depth-sorted with characters. Sprites are
// anchored bottom-center: the sprite's bottom edge sits on the bottom edge of
// its anchor tile (tx, ty). Missing sprite keys are skipped silently —
// the world must render cleanly before the asset PNGs land.

import { TILE_SIZE } from "./constants";
import { getCachedSprite } from "./sprite-sheet";
import { WORLD_PROPS } from "@/data/world-props";

// Local copy of the shared contract — world-props.ts is created concurrently
// by another agent, so the renderer must not depend on it at compile time.
export interface WorldProp {
  key: string;
  tx: number;
  ty: number;
}

/** Known prop pixel sizes (shared asset-key contract). */
export const PROP_SIZES: Record<string, { w: number; h: number }> = {
  prop_tree_large: { w: 48, h: 64 },
  prop_tree_small: { w: 32, h: 48 },
  prop_rock_1: { w: 16, h: 16 },
  prop_rock_2: { w: 16, h: 16 },
  prop_rock_3: { w: 16, h: 16 },
  prop_bush_1: { w: 16, h: 16 },
  prop_bush_2: { w: 16, h: 16 },
  prop_stump: { w: 16, h: 16 },
  prop_signpost: { w: 16, h: 32 },
  prop_flowerbed: { w: 16, h: 16 },
};

/** Tallest known prop — used as a viewport-culling margin. */
export const MAX_PROP_HEIGHT = 64;

// Placement data, sorted by ty so the renderer can merge-walk it against the
// Y-sorted character list without re-sorting per frame.
const worldProps: WorldProp[] = [...WORLD_PROPS].sort((a, b) => a.ty - b.ty);

/** All placed props, sorted by anchor row (ty) ascending. */
export function getWorldProps(): readonly WorldProp[] {
  return worldProps;
}

/** World-space Y of the prop's baseline (bottom edge of the anchor tile). */
export function propBaselineY(prop: WorldProp): number {
  return (prop.ty + 1) * TILE_SIZE;
}

/**
 * Draw one prop, bottom-center anchored on its tile. Returns silently when
 * the sprite isn't in the cache yet.
 */
export function drawWorldProp(
  ctx: CanvasRenderingContext2D,
  prop: WorldProp,
): void {
  const img = getCachedSprite(prop.key);
  if (!img) return; // asset not landed yet — skip silently
  const size = PROP_SIZES[prop.key];
  const w = size ? size.w : img.naturalWidth || TILE_SIZE;
  const h = size ? size.h : img.naturalHeight || TILE_SIZE;
  const px = Math.round(prop.tx * TILE_SIZE + TILE_SIZE / 2 - w / 2);
  const py = (prop.ty + 1) * TILE_SIZE - h;
  ctx.drawImage(img, px, py, w, h);
}
